import type { SVGProps, ReactNode, FC } from 'react'

/**
 * Centralized Icon Component Library
 * All icons include proper accessibility attributes
 */

/** Props for icon components */
interface IconProps extends SVGProps<SVGSVGElement> {
  className?: string
  ariaLabel?: string
}

/** Icon component type */
type IconComponent = FC<IconProps> & { displayName: string }

// Icon wrapper factory
const createIcon = (pathData: ReactNode, displayName: string): IconComponent => {
  const Icon: IconComponent = ({ className = 'w-6 h-6', ariaLabel, ...props }: IconProps) => {
    const isDecorative = !ariaLabel

    return (
      <svg
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
        aria-hidden={isDecorative}
        aria-label={ariaLabel}
        role={isDecorative ? 'presentation' : 'img'}
        {...props}
      >
        {pathData}
      </svg>
    )
  }
  Icon.displayName = displayName
  return Icon
}

// Navigation icons
export const HomeIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />,
  'HomeIcon'
)

export const UserIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />,
  'UserIcon'
)

export const UsersIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />,
  'UsersIcon'
)

export const SettingsIcon = createIcon(
  <>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </>,
  'SettingsIcon'
)

// Clock/Time icons
export const ClockIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'ClockIcon'
)

export const ClockInIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />,
  'ClockInIcon'
)

export const ClockOutIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />,
  'ClockOutIcon'
)

// Status icons
export const CheckIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />,
  'CheckIcon'
)

export const CheckCircleIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'CheckCircleIcon'
)

export const XIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />,
  'XIcon'
)

export const XCircleIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'XCircleIcon'
)

export const WarningIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />,
  'WarningIcon'
)

export const InfoIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'InfoIcon'
)

// Action icons
export const PlayIcon = createIcon(
  <>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </>,
  'PlayIcon'
)

export const PauseIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'PauseIcon'
)

export const RefreshIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />,
  'RefreshIcon'
)

export const TrashIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />,
  'TrashIcon'
)

export const EditIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
  'EditIcon'
)

// Navigation arrows
export const ChevronLeftIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />,
  'ChevronLeftIcon'
)

export const ChevronRightIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />,
  'ChevronRightIcon'
)

export const ChevronDownIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />,
  'ChevronDownIcon'
)

export const ChevronUpIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />,
  'ChevronUpIcon'
)

// Media icons
export const CameraIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />,
  'CameraIcon'
)

export const PhotoIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />,
  'PhotoIcon'
)

// Location icons
export const MapPinIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />,
  'MapPinIcon'
)

export const MapIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />,
  'MapIcon'
)

// Finance icons
export const CurrencyIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'CurrencyIcon'
)

// Document icons
export const DocumentIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  'DocumentIcon'
)

export const ClipboardIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />,
  'ClipboardIcon'
)

// UI icons
export const MenuIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />,
  'MenuIcon'
)

export const LogoutIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />,
  'LogoutIcon'
)

export const SunIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />,
  'SunIcon'
)

export const MoonIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />,
  'MoonIcon'
)

export const WifiOffIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />,
  'WifiOffIcon'
)

export const WifiIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />,
  'WifiIcon'
)

export const SyncIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />,
  'SyncIcon'
)

export const CloudUploadIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />,
  'CloudUploadIcon'
)

export const CloudOfflineIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M9.75 9.75a4 4 0 015.568 5.568M6.12 8.88A5 5 0 006 10a5 5 0 005 5h2M12 10V6a4 4 0 00-4 4" />,
  'CloudOfflineIcon'
)

export const ExclamationCircleIcon = createIcon(
  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
  'ExclamationCircleIcon'
)
